import { createAsyncThunk } from '@reduxjs/toolkit';
import { getRejectedValues, useAxios } from '../utilities/axios';

export const createCategory = createAsyncThunk('category/create', async (data: any, { getState, rejectWithValue }) => {
    try {
        const state: any = getState();
        if (!state.auth.accessToken) return;
        if (!state.tenants.currentTenant) return;

        const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant, state.teams.currentTeam);
        const response = await axios.post('api/v1/categories', data);

        return response.data;
    } catch (error: any) {
        return rejectWithValue(getRejectedValues(error));
    }
});

export const createCategoryNested = createAsyncThunk('category/create/nested', async (data: any, { getState, rejectWithValue }) => {
    try {
        const state: any = getState();
        if (!state.auth.accessToken) return;
        if (!state.tenants.currentTenant) return;

        const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant, state.teams.currentTeam);
        const response = await axios.post('api/v1/categories/nested', data);

        return response.data;
    } catch (error: any) {
        return rejectWithValue(getRejectedValues(error));
    }
});

export const fetch = createAsyncThunk('category/fetch', async (id: any, { getState, rejectWithValue }) => {
    try {
        const state: any = getState();
        if (!state.auth.accessToken) return;
        if (!state.tenants.currentTenant) return;
        const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant, state.teams.currentTeam);

        const response = await axios.get(`api/v1/categories/${id}`);
        return response.data;
    } catch (error: any) {
        return rejectWithValue(getRejectedValues(error));
    }
});

export const fetchCategories = createAsyncThunk('category/list', async (parent_id: any | null = null, { getState, rejectWithValue }) => {
    try {
        const state: any = getState();
        if (!state.auth.accessToken) return;
        if (!state.tenants.currentTenant) return;
        const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant, state.teams.currentTeam);

        let params: any = {};
        if (parent_id) params.parent_id = parent_id;
        const fetchParams = state.categories.params;
        if (fetchParams.excluded_ids) params.excluded_ids = fetchParams.excluded_ids.join(',');

        const response = await axios.get('api/v1/list/categories', { params: params });

        return response.data;
    } catch (error: any) {
        return rejectWithValue(getRejectedValues(error));
    }
});

export const fetchCategoriesMap = createAsyncThunk('category/map', async (parent_id: any | null = null, { getState, rejectWithValue }) => {
    try {
        const state: any = getState();
        if (!state.auth.accessToken) return;
        if (!state.tenants.currentTenant) return;
        const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant, state.teams.currentTeam);

        let params: any = {};
        if (parent_id) params.parent_id = parent_id;

        const response = await axios.get('api/v1/list/categories', { params: params });

        return response.data;
    } catch (error: any) {
        return rejectWithValue(getRejectedValues(error));
    }
});

export const renameCategory = createAsyncThunk('category/rename', async (data: { id: string, name: string }, { getState, rejectWithValue }) => {
    try {
        const state: any = getState();
        if (!state.auth.accessToken) return;
        if (!state.tenants.currentTenant) return;
        const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

        const response = await axios.post(`api/v1/categories/${data.id}/rename`, data);
        return response.data;
    } catch (error: any) {
        return rejectWithValue(getRejectedValues(error));
    }
});

export const moveCategory = createAsyncThunk('category/move', async (arg: { id: string, parent_id: number | null, suppressError?: boolean }, { getState, rejectWithValue }) => {
    try {
        const state: any = getState();
        if (!state.auth.accessToken) return;
        if (!state.tenants.currentTenant) return;
        const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

        const { suppressError, ...data } = arg;
        const response = await axios.post(`api/v1/categories/${data.id}/move`, data);
        return response.data;
    } catch (error: any) {
        const errorPayload = getRejectedValues(error);
        if (arg.suppressError) {
            errorPayload.suppressError = true;
        }
        return rejectWithValue(errorPayload);
    }
});

export const softDeleteCategory = createAsyncThunk('category/softDelete', async (id: any, { getState, rejectWithValue }) => {
    try {
        const state: any = getState();
        if (!state.auth.accessToken) return;
        if (!state.tenants.currentTenant) return;
        const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

        const response = await axios.delete(`api/v1/categories/${id}`);
        return response.data;
    } catch (error: any) {
        return rejectWithValue(getRejectedValues(error));
    }
});

export const foreverDeleteCategory = createAsyncThunk('posts/foreverDeleteCategory', async (id: any, { getState, rejectWithValue }) => {
    try {
        const state: any = getState();
        if (!state.auth.accessToken) return;
        if (!state.tenants.currentTenant) return;
        const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

        const response = await axios.delete(`api/v1/categories/${id}/forever`);
        return response.data;
    } catch (error: any) {
        return rejectWithValue(getRejectedValues(error));
    }
});

export const restoreDeletedCategory = createAsyncThunk('posts/restoreDeletedCategory', async (id: any, { getState, rejectWithValue }) => {
    try {
        const state: any = getState();
        if (!state.auth.accessToken) return;
        if (!state.tenants.currentTenant) return;
        const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

        const response = await axios.post(`api/v1/categories/${id}/restore`);
        return response.data;
    } catch (error: any) {
        return rejectWithValue(getRejectedValues(error));
    }
});

export const fetchUsers = createAsyncThunk('category/fetchUsers', async (id: any, { getState, rejectWithValue }) => {
    try {
        const state: any = getState();

        if (!state.auth.accessToken) return;
        if (!state.tenants.currentTenant) return;
        const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

        const response = await axios.get(`api/v1/categories/${id}/users`);
        return response.data;
    } catch (error: any) {
        return rejectWithValue(getRejectedValues(error));
    }
});

export const smx3 = createAsyncThunk('category/smx3', async (id: any, { getState, rejectWithValue }) => {
    try {
        const state: any = getState();
        if (!state.auth.accessToken) return;
        if (!state.tenants.currentTenant) return;
        const axios = useAxios(state.auth.accessToken, state.tenants.currentTenant);

        const response = await axios.get(`api/v1/categories/smx3/${id}`);
        return response.data;
    } catch (error: any) {
        return rejectWithValue(getRejectedValues(error));
    }
});
